// src/FleetOverview.jsx
//
// The lifetime story: "what has the fleet achieved". Deliberately complements
// FleetStats ("is the fleet OK today") — no health KPIs here, no all-time
// totals there. All-time numbers are legitimate content on this page, but
// every one gets context and honest labels.
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, LogOut } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import { C, PAGE_BG, CARD_BG, CARD_BORDER } from "./fleet-stats/constants.js";
import { flattenResults } from "./fleet-stats/utils/flattenResults.js";
import { classifyAudience } from "./fleet-stats/utils/audience.js";
import { fmtNum, fmtInt } from "./fleet-stats/utils/format.js";
import { brandButton, hoverNeon, unhoverNeon } from "./fleet-stats/components/SharedUI.jsx";
import { HeadlineSentence } from "./fleet-stats/components/HeadlineSentence.jsx";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const LINE_BLUE = "#4a9ebb";

const TOOLTIP_STYLE = {
  contentStyle: {
    background: C.deepBlue,
    border: `1px solid ${C.grey3}`,
    borderRadius: 10,
    color: C.offWhite,
    fontSize: 12,
    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
  },
  cursor: { fill: "rgba(255,255,255,0.04)" },
};

function monthLabel(ym) {
  return `${MONTH_NAMES[parseInt(ym.slice(5, 7), 10) - 1]} '${ym.slice(2, 4)}`;
}

function Overline({ children }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: C.grey2 }}>
      {children}
    </div>
  );
}

export default function FleetOverview({ onBack, userEmail, onSignOut }) {
  const [raw, setRaw] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetch(`/robot_stats.json?v=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.json())
      .then(setRaw)
      .catch((e) => setLoadError(String(e)));
  }, []);

  // Lifetime view covers customers + partners; internal sites never count
  const rows = useMemo(
    () => flattenResults(raw).filter((r) => classifyAudience(r.client, r.siteName) !== "internal"),
    [raw]
  );

  const kpis = useMemo(() => {
    if (!rows.length) return null;
    const robotSet = new Set();
    const siteSet = new Set();
    let totalDistM = 0;
    let totalMovingS = 0;
    let maxDate = rows[0].date;
    let minDate = rows[0].date;

    for (const r of rows) {
      robotSet.add(r.robotId);
      siteSet.add(r.siteName);
      totalDistM += r.distance_m;
      totalMovingS += r.moving_time_s;
      if (r.date > maxDate) maxDate = r.date;
      if (r.date < minDate) minDate = r.date;
    }

    return {
      totalRobots: robotSet.size,
      totalKm: totalDistM / 1000,
      totalHours: totalMovingS / 3600,
      totalSites: siteSet.size,
      maxDate,
      minDate,
    };
  }, [rows]);

  /* ---- headline: the lifetime takeaway ---- */
  const headline = useMemo(() => {
    if (!kpis) return null;
    const sinceLabel = `${MONTH_NAMES[parseInt(kpis.minDate.slice(5, 7), 10) - 1]} ${kpis.minDate.slice(0, 4)}`;
    return {
      segments: [
        { text: "The fleet has driven ", tone: "plain" },
        { text: `${fmtNum(kpis.totalKm, 0)} km`, tone: "value" },
        { text: " across ", tone: "plain" },
        { text: fmtInt(kpis.totalSites), tone: "value" },
        { text: ` sites since ${sinceLabel}.`, tone: "plain" },
      ],
      context: [
        { text: `${fmtInt(kpis.totalRobots)} robots deployed to date` },
        { text: `${fmtNum(kpis.totalHours, 0)} h total driving time` },
        { text: "customers & partners · internal sites excluded" },
        { text: `daily batch · data through ${kpis.maxDate}` },
      ],
    };
  }, [kpis]);

  /* ---- monthly series (complete months only — a partial month always reads
         as a crash and poisons the trend) ---- */
  const monthlyActivity = useMemo(() => {
    if (!kpis) return [];
    const lastYM = kpis.maxDate.slice(0, 7);
    const monthEnd = new Date(Date.UTC(+lastYM.slice(0, 4), +lastYM.slice(5, 7), 0));
    const isComplete = kpis.maxDate === monthEnd.toISOString().slice(0, 10);
    const endD = new Date(lastYM + "-01T00:00:00Z");
    if (!isComplete) endD.setUTCMonth(endD.getUTCMonth() - 1);
    const endYM = endD.toISOString().slice(0, 7);
    const startD = new Date(endYM + "-01T00:00:00Z");
    startD.setUTCMonth(startD.getUTCMonth() - 11);
    const startYM = startD.toISOString().slice(0, 7);

    const byMonth = new Map();
    for (const r of rows) {
      const ym = r.date.slice(0, 7);
      if (ym < startYM || ym > endYM) continue;
      const cur = byMonth.get(ym) || { ym, km: 0, robots: new Set() };
      cur.km += r.distance_m / 1000;
      if (r.distance_m > 0 || r.moving_time_s > 0) cur.robots.add(r.robotId);
      byMonth.set(ym, cur);
    }
    return Array.from(byMonth.values())
      .sort((a, b) => (a.ym < b.ym ? -1 : 1))
      .map((d) => ({
        ym: d.ym,
        km: +d.km.toFixed(0),
        activeRobots: d.robots.size,
        label: monthLabel(d.ym),
      }));
  }, [rows, kpis]);

  /* ---- all-time leaderboards ---- */
  const topSites = useMemo(() => {
    const bySite = new Map();
    for (const r of rows) {
      const cur = bySite.get(r.siteName) || { name: r.siteName, km: 0 };
      cur.km += r.distance_m / 1000;
      bySite.set(r.siteName, cur);
    }
    return Array.from(bySite.values())
      .sort((a, b) => b.km - a.km)
      .slice(0, 10)
      .map((s) => ({ ...s, km: +s.km.toFixed(1) }));
  }, [rows]);

  const topRobots = useMemo(() => {
    const byRobot = new Map();
    for (const r of rows) {
      const cur = byRobot.get(r.robotId) || { name: r.robotName, site: r.siteName, km: 0 };
      cur.km += r.distance_m / 1000;
      byRobot.set(r.robotId, cur);
    }
    return Array.from(byRobot.values())
      .sort((a, b) => b.km - a.km)
      .slice(0, 10)
      .reverse()
      .map((r) => {
        const label = `${r.name} (${r.site})`;
        return {
          km: +r.km.toFixed(1),
          name: label.length > 28 ? label.slice(0, 27) + "…" : label,
          robotName: r.name,
          siteName: r.site,
        };
      });
  }, [rows]);

  if (loadError) {
    return (
      <div className="min-h-screen px-6 py-10" style={{ background: PAGE_BG, color: C.offWhite }}>
        <button onClick={onBack} style={{ color: C.grey2 }}>← Back</button>
        <p className="mt-4" style={{ color: C.red }}>Failed to load: {loadError}</p>
      </div>
    );
  }

  if (!raw || !kpis) {
    return (
      <div className="min-h-screen px-6 py-10" style={{ background: PAGE_BG, color: C.offWhite }}>
        <p style={{ color: C.grey2 }}>Loading fleet overview…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-10" style={{ background: PAGE_BG, color: C.offWhite }}>
      <div className="max-w-[1240px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <button
              onClick={onBack}
              className="mb-3 inline-flex items-center gap-2 text-sm transition-colors"
              style={{ color: C.grey1 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}
            >
              <ArrowLeft size={16} /> Back to site directory
            </button>
            <h1 className="text-3xl font-bold mb-1" style={{ color: C.offWhite }}>Fleet overview — lifetime</h1>
            <p className="text-sm" style={{ color: C.grey2 }}>
              {kpis.minDate} → {kpis.maxDate} · Cloud sites only
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm mt-1">
            {userEmail && <span style={{ color: C.grey2 }} className="px-2">{userEmail}</span>}
            {onSignOut && (
              <button
                onClick={onSignOut}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
                style={brandButton()}
                onMouseEnter={hoverNeon}
                onMouseLeave={unhoverNeon}
              >
                <LogOut size={16} /> Sign out
              </button>
            )}
          </div>
        </div>

        {/* The lifetime takeaway */}
        <HeadlineSentence headline={headline} />

        {/* ── Over time ── */}
        <Overline>Over time</Overline>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">

          {/* Monthly distance */}
          <div className="rounded-2xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            <div className="text-sm font-semibold" style={{ color: C.offWhite }}>
              Monthly distance
            </div>
            <div className="text-xs mb-3" style={{ color: C.grey2 }}>
              Last 12 complete months — the current partial month is excluded
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyActivity} barSize={16} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke={C.grey3} strokeOpacity={0.15} vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: C.grey2, fontSize: 10 }} tickLine={false} axisLine={false} interval={0} />
                  <YAxis tick={{ fill: C.grey2, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE.contentStyle}
                    cursor={TOOLTIP_STYLE.cursor}
                    itemStyle={{ color: C.offWhite }}
                    formatter={(v) => [`${fmtNum(v, 0)} km`, "Distance"]}
                  />
                  <Bar dataKey="km" fill={C.strongBlue} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Robots active per month */}
          <div className="rounded-2xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            <div className="text-sm font-semibold" style={{ color: C.offWhite }}>
              Robots active per month
            </div>
            <div className="text-xs mb-3" style={{ color: C.grey2 }}>
              Distinct robots that drove at least once in the month
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyActivity} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="robotsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={LINE_BLUE} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={LINE_BLUE} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={C.grey3} strokeOpacity={0.15} vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: C.grey2, fontSize: 10 }} tickLine={false} axisLine={false} interval={1} />
                  <YAxis tick={{ fill: C.grey2, fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE.contentStyle}
                    cursor={{ stroke: C.grey3, strokeWidth: 1 }}
                    itemStyle={{ color: C.offWhite }}
                    formatter={(v) => [`${v} robots`, "Active"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="activeRobots"
                    stroke={LINE_BLUE}
                    strokeWidth={2}
                    fill="url(#robotsGrad)"
                    dot={{ fill: LINE_BLUE, r: 3, strokeWidth: 0 }}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ── All-time leaderboards ── */}
        <Overline>All-time leaderboards</Overline>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">

          {/* Top sites */}
          <div className="rounded-2xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            <div className="text-sm font-semibold" style={{ color: C.offWhite }}>
              Top 10 sites by distance
            </div>
            <div className="text-xs mb-3" style={{ color: C.grey2 }}>
              Total km since first data — long-deployed sites naturally lead
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={topSites} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={C.grey3} strokeOpacity={0.15} horizontal={false} />
                  <XAxis type="number" tick={{ fill: C.grey2, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fill: C.grey1, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE.contentStyle}
                    cursor={TOOLTIP_STYLE.cursor}
                    itemStyle={{ color: C.offWhite }}
                    formatter={(v) => [`${fmtNum(v, 1)} km`, "Distance"]}
                  />
                  <Bar dataKey="km" fill={C.strongBlue} radius={[0, 3, 3, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top robots */}
          <div className="rounded-2xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            <div className="text-sm font-semibold" style={{ color: C.offWhite }}>
              Top 10 robots by distance
            </div>
            <div className="text-xs mb-3" style={{ color: C.grey2 }}>
              Total km since first data
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={topRobots} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={C.grey3} strokeOpacity={0.15} horizontal={false} />
                  <XAxis type="number" tick={{ fill: C.grey2, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" width={170} tick={{ fill: C.grey1, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE.contentStyle}
                    cursor={TOOLTIP_STYLE.cursor}
                    itemStyle={{ color: C.offWhite }}
                    formatter={(v, _name, props) => [
                      `${fmtNum(v, 1)} km`,
                      `${props.payload.robotName} — ${props.payload.siteName}`,
                    ]}
                  />
                  <Bar dataKey="km" fill={C.strongBlue} radius={[0, 3, 3, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
